import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { FaMoneyBillWave, FaArrowLeft } from "react-icons/fa";
import StatusChip from "../../Common/StatusChip";
import TransactionDetailModal from "../../../Common/TransactionDetailModal";
import { GetAllStockApi } from "../../../Api/Store/Stock";
import { fetchGeneralLedgerTransactions } from "../../../Redux/actions/AccountReports/reports";

const StockDetailsPage = () => {
  const { materialId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const generalLedger = useSelector((state) => state.generalLedger);

  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openTransactionModal, setOpenTransactionModal] = useState(false);

  const viewTransactions = (usage) => {
    const voucherType = usage.TransactionType === "ADJUSTMENT" ? "Stock Adjustment" : "Gatepass";
    // null = no project filter: pulls every project this voucher touched, not just one leg.
    dispatch(fetchGeneralLedgerTransactions(usage.ReferenceId, voucherType, null));
    setOpenTransactionModal(true);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await GetAllStockApi(materialId);
        if (res?.data?.IsSuccess) {
          setStock((res.data.Data || [])[0] || null);
        } else {
          toast.error(res?.data?.Message || "Failed to load stock");
        }
      } catch (err) {
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [materialId]);

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!stock) {
    return <div className="p-4">No stock found for this material.</div>;
  }

  return (
    <div>
      <div className="row">
        <div className="col-md-12 text-left page_heading">
          <div className="d-flex align-items-center">
            <FaArrowLeft
              size={16}
              style={{ cursor: "pointer", marginRight: "12px" }}
              title="Back"
              onClick={() => history.goBack()}
            />
            <h4 className="m-0">Stock Details — {stock.ItemName}</h4>
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-md-2">
          <small className="text-muted d-block">Available Quantity</small>
          <div>
            {stock.Quantity} {stock.UomName}
          </div>
        </div>
        <div className="col-md-2">
          <small className="text-muted d-block">WAC (per {stock.UomName})</small>
          <div>{Number(stock.Wac ?? 0).toLocaleString()}</div>
        </div>
        <div className="col-md-2">
          <small className="text-muted d-block">Last Purchase Price (per {stock.UomName})</small>
          <div>{Number(stock.LastPurchasePrice ?? 0).toLocaleString()}</div>
        </div>
        <div className="col-md-2">
          <small className="text-muted d-block">Restock Level</small>
          <div>
            {stock.RestockLevel} {stock.UomName}
          </div>
        </div>
        <div className="col-md-2">
          <small className="text-muted d-block">Status</small>
          <StatusChip
            status={stock.BelowRestock ? "Below Restock" : "OK"}
            type={stock.BelowRestock ? "warning" : "success"}
          />
        </div>
        <div className="col-md-2">
          <small className="text-muted d-block">Updated At</small>
          <div>{stock.UpdatedAt ? moment(stock.UpdatedAt).format("MMM DD YYYY hh:mm A") : ""}</div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-md-12">
          <h5 style={{ color: "#056363" }}>Batches</h5>
          <table className="table table-bordered table-hover" style={{ fontSize: "13px" }}>
            <thead style={{ backgroundColor: "#E8FBFB" }}>
              <tr>
                <th>Sr</th>
                <th>Batch No (GRN)</th>
                <th>Qty Received</th>
                <th>Qty Consumed</th>
                <th>Remaining</th>
                <th>Unit Price (per {stock.UomName})</th>
                <th>Received At</th>
              </tr>
            </thead>
            <tbody>
              {(stock.Batches || []).map((batch, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{batch.BatchNo}</td>
                  <td>
                    {batch.Qty} {stock.UomName}
                  </td>
                  <td>
                    {batch.QtConsumed} {stock.UomName}
                  </td>
                  <td>
                    {batch.Remaining} {stock.UomName}
                  </td>
                  <td>{Number(batch.Price ?? 0).toLocaleString()}</td>
                  <td>
                    {batch.CreatedAt ? moment(batch.CreatedAt).format("MMM DD YYYY hh:mm A") : ""}
                  </td>
                </tr>
              ))}
              {(stock.Batches || []).length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-3">
                    No batches
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-md-12">
          <h5 style={{ color: "#056363" }}>Usage History</h5>
          <table className="table table-bordered table-hover" style={{ fontSize: "13px" }}>
            <thead style={{ backgroundColor: "#E8FBFB" }}>
              <tr>
                <th>Sr</th>
                <th>Date</th>
                <th>Source</th>
                <th>Project</th>
                <th>Quantity</th>
                <th>Unit Cost (per {stock.UomName})</th>
                <th>Total Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(stock.UsageHistory || []).map((usage, idx) => {
                const isAdjustment = usage.TransactionType === "ADJUSTMENT";
                const source = isAdjustment
                  ? usage.Quantity >= 0
                    ? "Adjustment (Increase)"
                    : "Adjustment (Decrease)"
                  : "Gate Pass";
                return (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>
                      {usage.TransactionDate
                        ? moment(usage.TransactionDate).format("MMM DD YYYY hh:mm A")
                        : ""}
                    </td>
                    <td>{source}</td>
                    <td>{usage.ProjectName}</td>
                    <td>
                      {usage.Quantity} {stock.UomName}
                    </td>
                    <td>{Number(usage.UnitCost ?? 0).toLocaleString()}</td>
                    <td>{Number(usage.TotalValue ?? 0).toLocaleString()}</td>
                    <td>
                      <span title="View Transactions" onClick={() => viewTransactions(usage)}>
                        <FaMoneyBillWave
                          size="1.1em"
                          style={{ cursor: "pointer", color: "#4ac8d1" }}
                        />
                      </span>
                    </td>
                  </tr>
                );
              })}
              {(stock.UsageHistory || []).length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-3">
                    No usage yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openTransactionModal && (
        <TransactionDetailModal
          title="Transaction Details"
          transactions={generalLedger?.transactionData || []}
          onClose={() => setOpenTransactionModal(false)}
          debitTotal={generalLedger?.transactionDebitTotal || 0}
          creditTotal={generalLedger?.transactionCreditTotal || 0}
          isLoading={generalLedger?.loading}
          columns={[
            {
              key: "date",
              label: "Date",
              accessor: (t) => moment(t?.EntryDate?.slice(0, 10)).format("MMM DD YYYY"),
            },
            {
              key: "project",
              label: "Project",
              accessor: (t) => t?.Project?.ProjectName,
            },
            {
              key: "account",
              label: "Account",
              accessor: (t) => t?.Account?.AccountTitle,
            },
            {
              key: "narration",
              label: "Narration",
              accessor: (t) => t?.Narration,
            },
          ]}
        />
      )}
    </div>
  );
};

export default StockDetailsPage;
